// frontend/src/utils/api.js
import { API_BASE_URL } from '../config';

/**
 * Decode a JWT's payload without verification. Returns null on parse failure.
 * Used client-side only to check expiry before deciding to auto-logout.
 */
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1];
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return false;
  return Date.now() / 1000 > payload.exp;
}

/**
 * Wrapper around fetch that attaches the auth token and handles auth
 * failures by logging the user out.
 *
 * Auto-logout fires ONLY when we have high-confidence evidence that the
 * session is dead:
 *   - The token is absent from localStorage, OR
 *   - The token's 'exp' claim has passed (verified client-side)
 *
 * A 401 with a still-valid-looking token is NOT treated as session death.
 * Those are typically endpoint-specific auth issues (e.g. an invitation
 * token being used against a regular endpoint) and the caller should
 * handle them without nuking the whole session.
 *
 * 403 responses never trigger logout (that's authorization, not auth).
 * Network and CORS errors throw from fetch and never reach this code —
 * callers should handle them with try/catch.
 */
export async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('auth_token');

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401 && endpoint !== '/auth/login') {
    if (_sessionIsDead(token) && window.location.pathname !== '/login') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_roles');
      window.location.href = '/login';
    }
  }

  return response;
}

function _sessionIsDead(token) {
  if (!token) return true;
  if (isTokenExpired(token)) return true;
  return false;
}
